"use client";

import { useEffect, useRef } from "react";
import {
  EditorContent,
  Extension,
  Node,
  mergeAttributes,
  useEditor,
  type Editor,
} from "@tiptap/react";
import { Plugin } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { cn } from "@/lib/utils";

/**
 * The email being written, Gmail-style: one editor holding what I write, my
 * signature under it, and - forwarding - the forwarded email under that. The
 * server cleans the HTML again before sending (lib/inbox/signature.ts).
 */

export type MailDraft = {
  html: string;
  /** What I wrote, without the signature or a forwarded email. */
  text: string;
};

/**
 * My signature: one block that can be deleted but not typed into, as Gmail
 * marks it. Its HTML is my saved signature, cleaned by the server.
 */
const Signature = Node.create({
  name: "signature",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes: () => ({ html: { default: "" } }),
  parseHTML: () => [
    {
      tag: "div.gmail_signature",
      getAttrs: (element) => ({ html: element.innerHTML }),
    },
  ],
  renderHTML: ({ node }) => {
    const div = document.createElement("div");
    div.className = "gmail_signature";
    div.innerHTML = node.attrs.html as string;
    return div;
  },
});

/** A forwarded email, which stays editable, as in Gmail. */
const ForwardedEmail = Node.create({
  name: "forwardedEmail",
  group: "block",
  content: "block+",
  defining: true,
  parseHTML: () => [{ tag: "div.gmail_quote" }],
  renderHTML: ({ HTMLAttributes }) => [
    "div",
    mergeAttributes(HTMLAttributes, { class: "gmail_quote" }),
    0,
  ],
});

/**
 * Nothing is written below the signature: only a forwarded email, and the
 * empty line above it, may follow.
 */
function nothingWrittenBelowSignature(doc: ProseMirrorNode): boolean {
  let below = false;
  let ok = true;
  doc.forEach((node) => {
    if (node.type.name === Signature.name) below = true;
    else if (
      below &&
      node.type.name !== ForwardedEmail.name &&
      !(node.type.name === "paragraph" && node.content.size === 0)
    ) {
      ok = false;
    }
  });
  return ok;
}

const SignatureLast = Extension.create({
  name: "signatureLast",
  addProseMirrorPlugins: () => [
    new Plugin({
      filterTransaction: (tr) =>
        !tr.docChanged || nothingWrittenBelowSignature(tr.doc),
    }),
  ],
});

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Plain text as editor lines, one paragraph per line. */
const textToLines = (text: string): string =>
  text
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");

/**
 * A new draft: what to start with, a blank line, then the signature - above
 * a forwarded email, where Gmail puts it.
 */
export function mailDraft({
  written = "",
  signatureHtml,
  forwarded,
}: {
  written?: string;
  signatureHtml: string;
  forwarded?: string;
}): MailDraft {
  const html = [
    textToLines(written),
    "<p></p>",
    signatureHtml
      ? `<div class="gmail_signature"><p>-- </p>${signatureHtml}</div>`
      : "",
    forwarded
      ? `<p></p><div class="gmail_quote">${textToLines(forwarded)}</div>`
      : "",
  ].join("");
  return { html, text: written };
}

/** The text outside the signature and forwarded email. */
function writtenText(editor: Editor): string {
  const lines: string[] = [];
  editor.state.doc.forEach((node) => {
    if (node.type.name === Signature.name) return;
    if (node.type.name === ForwardedEmail.name) return;
    lines.push(node.textBetween(0, node.content.size, "\n", "\n"));
  });
  return lines.join("\n").trim();
}

const draftOf = (editor: Editor): MailDraft => ({
  html: editor.getHTML(),
  text: writtenText(editor),
});

export function MailEditor({
  value,
  onChange,
  onSubmit,
  autoFocus,
  className,
}: {
  value: string;
  onChange: (draft: MailDraft) => void;
  /** Cmd/Ctrl+Enter. */
  onSubmit?: () => void;
  autoFocus?: boolean;
  className?: string;
}) {
  // The editor is created once; these keep its handlers current.
  const onChangeRef = useRef(onChange);
  const onSubmitRef = useRef(onSubmit);
  onChangeRef.current = onChange;
  onSubmitRef.current = onSubmit;

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        // No empty line forced after the signature, and no cursor there.
        trailingNode: false,
        gapcursor: false,
        link: { openOnClick: false, autolink: true },
      }),
      Image.configure({ inline: true }),
      Signature,
      ForwardedEmail,
      SignatureLast,
    ],
    content: value,
    // The cursor starts above the signature, as in Gmail.
    autofocus: autoFocus ? "start" : false,
    editorProps: {
      attributes: {
        class: cn(
          "min-h-[140px] px-4 py-3 text-sm leading-relaxed outline-none",
          "[&_p]:my-0 [&_a]:text-blue-600 [&_a]:underline",
          "[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6",
          "[&_.gmail_quote]:text-gray-600",
          className,
        ),
      },
      handleKeyDown: (_view, event) => {
        if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) {
          return false;
        }
        event.preventDefault();
        onSubmitRef.current?.();
        return true;
      },
    },
    onCreate: ({ editor }) => onChangeRef.current(draftOf(editor)),
    onUpdate: ({ editor }) => onChangeRef.current(draftOf(editor)),
  });

  // A draft set from outside (a new reply, forwarding, the assistant's
  // draft, discarding) replaces what the editor holds.
  useEffect(() => {
    if (!editor || editor.getHTML() === value) return;
    editor.commands.setContent(value, { emitUpdate: false });
    onChangeRef.current(draftOf(editor));
    if (autoFocus) editor.commands.focus("start");
  }, [editor, value, autoFocus]);

  return <EditorContent editor={editor} />;
}
